import { capitalizeString } from '@blisspointmedia/bpm-types/dist/StyleUtils';
import { ModelConfig } from '../hroas-types';
import { HroasResultsModel } from './hroasResults';

export type ModelFamily = 'rmmm' | 'geommm' | 'brand_equity';

export interface Slot {
  slotId: string;
  family: ModelFamily;
  taxonomyModelType: string;
  modelScope: string;
  label: string;
  decayKey?: string;
  // Scenario Planning artifacts this slot lacks (empty/absent = ready). Model Results ignores it.
  spMissingArtifacts?: string[];
}

export interface SlotRow {
  model_type?: string;
  model_scope?: string;
  taxonomy_model_type?: string;
}

const MODEL_TYPE_TO_FAMILY: Record<string, ModelFamily> = {
  rmmm: 'rmmm',
  dtc: 'rmmm',
  commerce: 'rmmm',
  'geo-mmm': 'geommm',
  geo_mmm: 'geommm',
  geommm: 'geommm',
  geo: 'geommm',
  brand_equity: 'brand_equity',
  'brand-equity': 'brand_equity',
  'brand-equity-attribution': 'brand_equity'
};

export const FAMILY_TO_DECAY_KEY: Record<ModelFamily, string> = {
  rmmm: 'online_revenue',
  geommm: 'instore_revenue',
  brand_equity: 'total_revenue'
};

const TAXONOMY_LABELS: Record<string, string> = {
  dtc: 'DTC',
  commerce: 'Commerce',
  geo: 'Geo',
  brand_equity: 'Brand Equity'
};

const SCOPE_ACRONYMS: Record<string, string> = {
  dtc: 'DTC',
  mmm: 'MMM'
};

export const familyOf = (modelType: string | undefined): ModelFamily | null => {
  const key = modelType?.toLowerCase();
  return key && Object.hasOwn(MODEL_TYPE_TO_FAMILY, key) ? MODEL_TYPE_TO_FAMILY[key] : null;
};

export const prettifyScope = (scope: string): string =>
  scope
    .split('_')
    .filter(Boolean)
    .map(word =>
      Object.hasOwn(SCOPE_ACRONYMS, word.toLowerCase())
        ? SCOPE_ACRONYMS[word.toLowerCase()]
        : capitalizeString(word)
    )
    .join(' ');

export const taxonomyLabelOf = (taxonomyModelType: string): string =>
  Object.hasOwn(TAXONOMY_LABELS, taxonomyModelType)
    ? TAXONOMY_LABELS[taxonomyModelType]
    : prettifyScope(taxonomyModelType);

const loggedErrors: Record<string, true> = {};

const logOnce = (message: string): void => {
  if (!loggedErrors[message]) {
    loggedErrors[message] = true;
    console.error(message);
  }
};

export const deriveSlots = (models: HroasResultsModel[] | undefined | null): Slot[] => {
  const slots: Slot[] = [];
  const seen: Record<string, true> = {};
  (models ?? []).forEach(model => {
    const taxonomyModelType = model.taxonomy_model_type?.toLowerCase();
    if (!taxonomyModelType) {
      logOnce(`HROAS skipped model "${model.model_type}" without taxonomy_model_type`);
      return;
    }
    const family = familyOf(model.model_type) ?? familyOf(taxonomyModelType);
    if (!family) {
      logOnce(`HROAS skipped model "${model.model_type}" with an unknown model_type`);
      return;
    }
    const modelScope = model.model_scope ?? '';
    const slotId = `${taxonomyModelType}:${modelScope}`;
    if (seen[slotId]) {
      logOnce(`HROAS skipped duplicate model slot "${slotId}"`);
      return;
    }
    seen[slotId] = true;
    slots.push({
      slotId,
      family,
      taxonomyModelType,
      modelScope,
      label: taxonomyLabelOf(taxonomyModelType),
      decayKey: model.decay_key,
      spMissingArtifacts: model.sp_missing_artifacts ?? []
    });
  });
  return slots.map(slot =>
    slots.filter(other => other.taxonomyModelType === slot.taxonomyModelType).length > 1
      ? { ...slot, label: `${slot.label} (${prettifyScope(slot.modelScope)})` }
      : slot
  );
};

export const slotsToModelConfigs = (slots: Slot[]): ModelConfig[] =>
  slots.map(slot => ({
    id: slot.slotId,
    label: slot.label,
    appLabel: slot.label,
    enabled: true,
    weight: 1
  }));

const slotOfRow = (row: SlotRow, slots: Slot[]): Slot | undefined => {
  const taxonomy = row.taxonomy_model_type?.toLowerCase();
  const family = familyOf(row.model_type);
  let candidates: Slot[] = [];
  if (taxonomy) {
    candidates = slots.filter(slot => slot.taxonomyModelType === taxonomy);
  } else if (family) {
    candidates = slots.filter(slot => slot.family === family);
  }
  const scoped = row.model_scope
    ? candidates.filter(slot => slot.modelScope === row.model_scope)
    : [];
  const pool = scoped.length > 0 ? scoped : candidates;
  return pool.length === 1 ? pool[0] : undefined;
};

export const tagRowsWithSlot = <T extends SlotRow>(
  rows: T[],
  slots: Slot[]
): (T & { slotId: string })[] => {
  const slotIdByRowKey: Record<string, string | undefined> = {};
  const tagged: (T & { slotId: string })[] = [];
  let droppedMultiSlot = 0;
  rows.forEach(row => {
    const rowKey = `${row.model_type}|${row.model_scope}|${row.taxonomy_model_type}`;
    if (!Object.hasOwn(slotIdByRowKey, rowKey)) {
      slotIdByRowKey[rowKey] = slotOfRow(row, slots)?.slotId;
    }
    const slotId = slotIdByRowKey[rowKey];
    if (slotId !== undefined) {
      tagged.push({ ...row, slotId });
    } else {
      const family = familyOf(row.model_type);
      if (family && familySlotIds(slots, family).length > 1) {
        droppedMultiSlot += 1;
      }
    }
  });
  if (import.meta.env.DEV && droppedMultiSlot > 0) {
    console.warn(
      `HROAS dropped ${droppedMultiSlot} rows of a multi-slot family that match no slot`
    );
  }
  return tagged;
};

export const familySlotIds = (slots: Slot[], family: ModelFamily): string[] =>
  slots.filter(slot => slot.family === family).map(slot => slot.slotId);

export const decayKeyOf = (slots: Slot[], slot: Slot): string | undefined =>
  slot.decayKey ??
  (familySlotIds(slots, slot.family).length === 1 ? FAMILY_TO_DECAY_KEY[slot.family] : undefined);

export const decaySlotIdByKey = (slots: Slot[]): Record<string, string> => {
  const slotIdByKey: Record<string, string> = {};
  slots.forEach(slot => {
    const key = decayKeyOf(slots, slot);
    if (key) {
      slotIdByKey[key] = slot.slotId;
    }
  });
  return slotIdByKey;
};
